import React, { useState, useEffect, useMemo, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";

// Ui components
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericButton from "@ui-components/Button/GenericButton";
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import {
  buildPredicate,
  FilterField,
  labelId,
  parseToken,
  ParsedFilter,
} from "kontrolEdge/helpers/advancedFilters";
import { makeSyslogFields } from "./syslogFieldSpec";

// Llamadas de syslog
import {
  getCTXRlistGlobal,
  getAvailableSyslogs,
  getSelectedSyslogs,
  saveSyslogs,
  SyslogItem,
} from "./request/request";

// Columnas
import {
  getAvailableColumns,
  getSelectedColumns,
  LOG_LEVELS,
} from "./SyslogData";
import { useAppSelector } from "@redux/hooks";
import {
  SkeletonTableRows,
  SkeletonLine,
} from "@ui-components/Skeleton/GenericSkeleton";
import GenericCard from "@ui-components/Card/GenericCard";

interface CtxrItem {
  id: string;
  name: string;
  description: string;
  subscriptionId: number;
}

function SyslogPage() {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [isFetching, setIsFetching] = useState(false);
  const { tenantId } = useAppSelector((state) => state.selection);
  const isLoadingDataRef = useRef(false);

  // Lista CTX-R
  const [fullCTXRlist, setFullCTXRlist] = useState<CtxrItem[]>([]);
  const [instanceOptions, setInstanceOptions] = useState<string[]>([]);
  const [selectedInstance, setSelectedInstance] = useState("");

  // Syslog items
  const [availableItems, setAvailableItems] = useState<SyslogItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<SyslogItem[]>([]);

  // Copia inicial (Undo)
  const [initialAvailable, setInitialAvailable] = useState<SyslogItem[]>([]);
  const [initialSelected, setInitialSelected] = useState<SyslogItem[]>([]);

  // Para checkboxes
  const [selectedAvailableIds, setSelectedAvailableIds] = useState<Set<number>>(
    new Set()
  );
  const [selectedSelectedIds, setSelectedSelectedIds] = useState<Set<number>>(
    new Set()
  );

  // Detectar cambios => Undo / Save
  const [hasChanges, setHasChanges] = useState(false);

  // Filtros Available y Selected con GenericSearchbar
  const [activeFiltersAvailable, setActiveFiltersAvailable] = useState<
    ParsedFilter[]
  >([]);
  const [activeFiltersSelected, setActiveFiltersSelected] = useState<
    ParsedFilter[]
  >([]);
  const syslogFields = useMemo(() => makeSyslogFields(), []);

  // Generate suggested keys from field labels
  const suggestedKeys = useMemo(() => {
    return syslogFields.map((f) => f.label);
  }, [syslogFields]);

  // Build maps for filter normalization (Available)
  const fieldByIdAvailable = useMemo(() => {
    const m = new Map<string, FilterField<SyslogItem>>();
    for (const f of syslogFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      m.set(id, f);
    }
    return m;
  }, [syslogFields]);

  const keyMapAvailable = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of syslogFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      const label = f.label.toLowerCase();
      m.set(id, id);
      m.set(label, id);
    }
    return m;
  }, [syslogFields]);

  // Normalize filters (Available)
  const normalizedFiltersAvailable = useMemo(() => {
    return activeFiltersAvailable.map((f) => {
      const token = f.op
        ? { ...f }
        : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMapAvailable.get(k) ?? labelId(token.key);
      }

      const field = token.key ? fieldByIdAvailable.get(token.key) : undefined;
      if (field?.type === "string") {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1];
          token.op = "=";
        } else {
          token.op = ":";
        }
      }
      return token;
    });
  }, [activeFiltersAvailable, keyMapAvailable, fieldByIdAvailable]);

  // Build predicate (Available)
  const predicateAvailable = useMemo(
    () => buildPredicate<SyslogItem>(normalizedFiltersAvailable, syslogFields),
    [normalizedFiltersAvailable, syslogFields]
  );

  // Build maps for filter normalization (Selected)
  const fieldByIdSelected = useMemo(() => {
    const m = new Map<string, FilterField<SyslogItem>>();
    for (const f of syslogFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      m.set(id, f);
    }
    return m;
  }, [syslogFields]);

  const keyMapSelected = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of syslogFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      const label = f.label.toLowerCase();
      m.set(id, id);
      m.set(label, id);
    }
    return m;
  }, [syslogFields]);

  // Normalize filters (Selected)
  const normalizedFiltersSelected = useMemo(() => {
    return activeFiltersSelected.map((f) => {
      const token = f.op
        ? { ...f }
        : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMapSelected.get(k) ?? labelId(token.key);
      }

      const field = token.key ? fieldByIdSelected.get(token.key) : undefined;
      if (field?.type === "string") {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1];
          token.op = "=";
        } else {
          token.op = ":";
        }
      }
      return token;
    });
  }, [activeFiltersSelected, keyMapSelected, fieldByIdSelected]);

  // Build predicate (Selected)
  const predicateSelected = useMemo(
    () => buildPredicate<SyslogItem>(normalizedFiltersSelected, syslogFields),
    [normalizedFiltersSelected, syslogFields]
  );

  // Apply predicates to filter data
  const filteredAvailable = useMemo(
    () => availableItems.filter(predicateAvailable),
    [availableItems, predicateAvailable]
  );

  const filteredSelected = useMemo(
    () => selectedItems.filter(predicateSelected),
    [selectedItems, predicateSelected]
  );

  // Revisar cambios
  useEffect(() => {
    const sameAvail =
      JSON.stringify(availableItems) === JSON.stringify(initialAvailable);
    const sameSel =
      JSON.stringify(selectedItems) === JSON.stringify(initialSelected);
    setHasChanges(!sameAvail || !sameSel);
  }, [availableItems, selectedItems, initialAvailable, initialSelected]);

  // Montar => cargar CTX-R
  useEffect(() => {
    if (isLoading || !isAuthenticated || !tenantId) return;

    const fetchCTXRList = async () => {
      try {
        setIsFetching(true);
        const token = await getAccessTokenSilently();
        const response = await getCTXRlistGlobal(-1, tenantId, token);
        const dataArray: CtxrItem[] = response.data || [];

        setFullCTXRlist(dataArray);
        const mapped = dataArray.map(
          (item) => `${item.name} (${item.description})`
        );
        setInstanceOptions(mapped);

        if (mapped.length > 0) {
          const first = mapped[0];
          setSelectedInstance(first);
          const found = dataArray.find(
            (x) => `${x.name} (${x.description})` === first
          );
          if (found) {
            await loadSyslogData(found.id);
          }
        }
      } catch (err) {
        console.error("Error fetching CTXR list:", err);
      } finally {
        setIsFetching(false);
      }
    };

    fetchCTXRList();
  }, [isLoading, isAuthenticated, getAccessTokenSilently, tenantId]);

  const handleInstanceSelect = async (instanceStr: string) => {
    // Prevent double calls: if already selected or loading, ignore
    if (selectedInstance === instanceStr || isLoadingDataRef.current) {
      return;
    }
    
    setSelectedInstance(instanceStr);
    const found = fullCTXRlist.find(
      (x) => `${x.name} (${x.description})` === instanceStr
    );
    if (found) {
      await loadSyslogData(found.id);
    }
  };

  const loadSyslogData = async (ctxrId: string) => {
    // Prevent concurrent calls
    if (isLoadingDataRef.current) {
      return;
    }
    
    try {
      isLoadingDataRef.current = true;
      setIsFetching(true);
      const token = await getAccessTokenSilently();

      const subscriptionId = 1;

      // Available
      const respAvail = await getAvailableSyslogs(
        subscriptionId,
        ctxrId,
        tenantId,
        token
      );
      const dataAvail = respAvail.data as SyslogItem[];

      // Selected
      const respSelected = await getSelectedSyslogs(
        subscriptionId,
        ctxrId,
        tenantId,
        token
      );
      const dataSelected = respSelected.data as SyslogItem[];

      setAvailableItems(dataAvail);
      setSelectedItems(dataSelected);

      setInitialAvailable(JSON.parse(JSON.stringify(dataAvail)));
      setInitialSelected(JSON.parse(JSON.stringify(dataSelected)));

      // Limpiar sets
      setSelectedAvailableIds(new Set());
      setSelectedSelectedIds(new Set());
    } catch (error) {
      console.error("Error loading syslog data =>", error);
    } finally {
      setIsFetching(false);
      isLoadingDataRef.current = false;
    }
  };

  // Toggle check
  const toggleAvailableId = (id: number) => {
    setSelectedAvailableIds((prev) => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  };

  const toggleSelectedId = (id: number) => {
    setSelectedSelectedIds((prev) => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  };

  // Mover flechas
  const handleMoveToSelected = () => {
    if (selectedAvailableIds.size === 0) return;
    const moving = availableItems.filter((it) =>
      selectedAvailableIds.has(it.id)
    );
    const remainAvail = availableItems.filter(
      (it) => !selectedAvailableIds.has(it.id)
    );
    const mergedSelected = [...selectedItems, ...moving];

    setAvailableItems(remainAvail);
    setSelectedItems(mergedSelected);
    setSelectedAvailableIds(new Set());
  };

  const handleMoveToAvailable = () => {
    if (selectedSelectedIds.size === 0) return;
    const moving = selectedItems.filter((it) => selectedSelectedIds.has(it.id));
    const remainSelected = selectedItems.filter(
      (it) => !selectedSelectedIds.has(it.id)
    );
    const newAvailable = [...availableItems, ...moving];

    setAvailableItems(newAvailable);
    setSelectedItems(remainSelected);
    setSelectedSelectedIds(new Set());
  };

  // Cambio de Level
  const handleLevelChange = (row: SyslogItem, newLevelStr: string) => {
    const newLevelIndex = LOG_LEVELS.indexOf(newLevelStr);
    setSelectedItems((prev) =>
      prev.map((it) =>
        it.id === row.id ? { ...it, level: newLevelIndex } : it
      )
    );
  };

  // Alerts
  const [undoAlertOpen, setUndoAlertOpen] = useState(false);

  const doActualUndo = () => {
    setAvailableItems(JSON.parse(JSON.stringify(initialAvailable)));
    setSelectedItems(JSON.parse(JSON.stringify(initialSelected)));
    setSelectedAvailableIds(new Set());
    setSelectedSelectedIds(new Set());
  };

  const handleUndo = () => {
    if (!hasChanges) return;
    setUndoAlertOpen(true);
  };

  const confirmUndo = () => {
    setUndoAlertOpen(false);
    doActualUndo();
    setSuccessAlertOpen(true);
    setAlertTitle("Discarded");
    setAlertDesc("All changes have been discarded.");
  };

  const cancelUndo = () => {
    setUndoAlertOpen(false);
  };

  // Save
  const [saveAlertOpen, setSaveAlertOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = () => {
    if (!hasChanges) return;
    setSaveAlertOpen(true);
  };

  const confirmSave = async () => {
    setSaveAlertOpen(false);
    setIsSaving(true);

    try {
      const found = fullCTXRlist.find(
        (x) => `${x.name} (${x.description})` === selectedInstance
      );
      if (!found) throw new Error("No CTX-R found to save.");

      const token = await getAccessTokenSilently();
      const subscriptionId = 1;

      await saveSyslogs(
        subscriptionId,
        found.id,
        tenantId,
        token,
        selectedItems
      );
      await loadSyslogData(found.id);

      // Exito => success alert
      setSuccessAlertOpen(true);
      setAlertTitle("Saved!");
      setAlertDesc("Your changes have been saved successfully.");
    } catch (err) {
      console.error("Error saving syslogs =>", err);
      setErrorAlertOpen(true);
      setErrorTitle("Error on save");
      setErrorDesc("Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const cancelSave = () => {
    setSaveAlertOpen(false);
  };

  // Success alert
  const [successAlertOpen, setSuccessAlertOpen] = useState(false);
  const [alertTitle, setAlertTitle] = useState("");
  const [alertDesc, setAlertDesc] = useState("");

  const closeSuccessAlert = () => {
    setSuccessAlertOpen(false);
  };

  // Error alert
  const [errorAlertOpen, setErrorAlertOpen] = useState(false);
  const [errorTitle, setErrorTitle] = useState("");
  const [errorDesc, setErrorDesc] = useState("");

  const closeErrorAlert = () => {
    setErrorAlertOpen(false);
  };

  // Columnas
  const availableColumns = getAvailableColumns(
    selectedAvailableIds,
    toggleAvailableId
  );
  const selectedColumns = getSelectedColumns(
    selectedSelectedIds,
    toggleSelectedId,
    handleLevelChange
  );

  return (
    <div className="relative">
      {/* HEADER */}
      <div className="mb-4 p-4 bg-semantic-bg-primary border border-semantic-border-secondary rounded-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
          <div className="flex flex-col gap-3 w-full sm:w-auto sm:flex-row sm:items-end sm:gap-0 sm:space-x-4">
            <div className="flex flex-col w-full sm:w-auto">
              {isFetching ? (
                <>
                  <SkeletonLine w={300} h={16} />
                </>
              ) : (
                <GenericDropdown
                  items={instanceOptions}
                  value={selectedInstance}
                  onSelect={handleInstanceSelect}
                  minWidthClass="min-w-full sm:min-w-[300px]"
                  widthClass="w-full sm:w-60"
                  label="CTX-R instance"
                  dropdownMaxHeightClass="max-h-48"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* CONTENIDO: 2 tablas + Filtros */}
      <div className="flex flex-col md:flex-row gap-6">
        {/* LADO IZQ: Available */}
        <div className="flex-1 min-w-0">
          <GenericCard
            isCollapsible={false}
            isLoading={isLoading}
            bodyContent={
              <>
                <div className="flex flex-col gap-2 mb-4">
                  <h3 className="text-semantic-text-primary font-medium">
                    Available
                  </h3>
                  {/* Filtro con GenericSearchbar */}
                  <GenericSearchbar
                    placeholder="Find by attribute or value (e.g., IP: 192.168.1.1)"
                    suggestedKeys={suggestedKeys}
                    onFiltersChange={setActiveFiltersAvailable}
                    liveFiltering={true}
                    debounceMs={200}
                  />
                </div>
                {isFetching ? (
                  <div className="p-4">
                    <SkeletonTableRows
                      rows={5}
                      cols={3}
                      colWidths={["40px", "150px", "200px"]}
                      rowHeight={15}
                      rowGapClassName="space-y-3"
                    />
                  </div>
                ) : (
                  <GenericTable
                    columns={availableColumns}
                    data={filteredAvailable}
                    initialRowsPerPage={10}
                    rowsPerPageOptions={[5, 10]}
                    tableClassName="bg-semantic-bg-primary"
                    width="100%"
                    rowKey={(row) => row.id}
                    onRowClick={(row) => toggleAvailableId(row.id)}
                    selectedRowKeys={new Set(selectedAvailableIds)}
                  />
                )}
              </>
            }
          />
        </div>

        {/* Flechas - Centradas (horizontales en mobile, verticales en md+) */}
        <div className="flex flex-row md:flex-col items-center justify-center gap-4 flex-shrink-0">
          <GenericButton
            label=""
            iconLeftName="arrowRight"
            variant="secondary"
            shape="ghost"
            size="md"
            onClick={handleMoveToSelected}
            disabled={selectedAvailableIds.size === 0}
          />
          <GenericButton
            label=""
            iconLeftName="arrowLeft"
            variant="secondary"
            shape="ghost"
            size="md"
            onClick={handleMoveToAvailable}
            disabled={selectedSelectedIds.size === 0}
          />
        </div>

        {/* LADO DER: Selected */}
        <div className="flex-1 min-w-0">
          <GenericCard
            isCollapsible={false}
            isLoading={isLoading}
            bodyContent={
              <>
                <div className="flex flex-col gap-2 mb-4">
                  <h3 className="text-semantic-text-primary font-medium">
                    Selected
                  </h3>
                  {/* Filtro con GenericSearchbar */}
                  <GenericSearchbar
                    placeholder="Find by attribute or value (e.g., IP: 192.168.1.1)"
                    suggestedKeys={suggestedKeys}
                    onFiltersChange={setActiveFiltersSelected}
                    liveFiltering={true}
                    debounceMs={200}
                  />
                </div>

                {isFetching ? (
                  <div className="p-4">
                    <SkeletonTableRows
                      rows={5}
                      cols={4}
                      colWidths={["40px", "150px", "200px", "140px"]}
                      rowHeight={15}
                      rowGapClassName="space-y-3"
                    />
                  </div>
                ) : (
                  <GenericTable
                    columns={selectedColumns}
                    data={filteredSelected}
                    initialRowsPerPage={10}
                    rowsPerPageOptions={[5, 10]}
                    tableClassName="bg-semantic-bg-primary"
                    width="100%"
                    rowKey={(row) => row.id}
                    onRowClick={(row) => toggleSelectedId(row.id)}
                    selectedRowKeys={new Set(selectedSelectedIds)}
                  />
                )}
              </>
            }
          />
        </div>
      </div>

      {/* BOTONES: Undo & Save */}
      <div className="mt-4 flex items-center justify-end gap-4">
        <GenericButton
          label="Undo"
          variant="secondary"
          size="md"
          onClick={handleUndo}
          disabled={!hasChanges}
        />
        <GenericButton
          label={isSaving ? "Saving..." : "Save"}
          variant="brand"
          size="md"
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
        />
      </div>

      {/* ALERTS */}
      {/* 1) Confirm Undo */}
      <GenericAlert
        isOpen={undoAlertOpen}
        onClose={cancelUndo}
        onConfirm={confirmUndo}
        title="Unsaved Changes"
        description="Are you sure you want to discard the changes?"
        cancelLabel="No"
        confirmLabel="Yes"
        variant="confirm"
      />

      {/* 2) Confirm Save */}
      <GenericAlert
        isOpen={saveAlertOpen}
        onClose={cancelSave}
        onConfirm={confirmSave}
        title="Save Changes"
        description="Are you sure you want to save the changes?"
        cancelLabel="No"
        confirmLabel="Yes"
        variant="confirm"
      />

      {/* 3) Success Alert */}
      <GenericAlert
        isOpen={successAlertOpen}
        onClose={closeSuccessAlert}
        onConfirm={closeSuccessAlert}
        title={alertTitle}
        description={alertDesc}
        confirmLabel="OK"
        variant="success"
        hideCancel={true}
      />

      {/* 4) Error Alert */}
      <GenericAlert
        isOpen={errorAlertOpen}
        onClose={closeErrorAlert}
        onConfirm={closeErrorAlert}
        title={errorTitle}
        description={errorDesc}
        confirmLabel="OK"
        variant="error"
        hideCancel={true}
      />
    </div>
  );
}

export default SyslogPage;
